// src/pages/PanelFirma/CartaPoderDetalle.js

import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Button, Chip, IconButton, Tooltip, CircularProgress,
    Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Cancel as CancelIcon,
    GetApp as DownloadIcon,
    Description as DescriptionIcon,
    VpnKey as TokenIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import ContextoExpediente from './ContextoExpediente';
import {
    panelFirmaDescargarPdfJWT,
    panelFirmaSubirPdfFirmadoJWT,
} from '../../api/endpoints';

const FIRMA_SERVICE_URL = process.env.REACT_APP_FIRMA_SERVICE_URL || 'http://127.0.0.1:5850';

const styles = {
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    splitView: {
        display: 'flex',
        gap: '16px',
        minHeight: 500,
        '@media (max-width: 899.95px)': {
            flexDirection: 'column',
        },
    },
    documentPanel: {
        flex: '0 0 58%',
        maxWidth: '58%',
        '@media (max-width: 899.95px)': {
            flex: '1 1 auto',
            maxWidth: '100%',
        },
    },
    contextPanel: {
        flex: '0 0 40%',
        maxWidth: '40%',
        '@media (max-width: 899.95px)': {
            flex: '1 1 auto',
            maxWidth: '100%',
        },
    },
    documentPreview: {
        padding: '24px',
        borderRadius: '4px',
        minHeight: 400,
        maxHeight: 600,
        overflowY: 'auto',
        '& p': { margin: '4px 0' },
    },
    metaRow: {
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        marginBottom: '8px',
    },
    actionsBar: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        padding: '16px',
    },
};

const CartaPoderDetalle = ({
    doc,
    idExpediente,
    onVolver,
    onRechazar,
    onGenerarPdf,
    onDescargarPdf,
    generandoPdf,
    onRefresh,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [servicioFirma, setServicioFirma] = useState({ disponible: null, tokens: 0 });
    const [firmaDialog, setFirmaDialog] = useState({ open: false, pin: '', firmando: false });

    useEffect(() => {
        const checkFirma = async () => {
            try {
                const resp = await fetch(`${FIRMA_SERVICE_URL}/status`, { signal: AbortSignal.timeout(2000) });
                if (resp.ok) {
                    const data = await resp.json().catch(() => ({}));
                    setServicioFirma({ disponible: true, tokens: data.tokensDetectados ?? 0 });
                } else {
                    setServicioFirma({ disponible: false, tokens: 0 });
                }
            } catch (err) {
                if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
                    try {
                        await fetch(`${FIRMA_SERVICE_URL}/status`, { mode: 'no-cors', signal: AbortSignal.timeout(2000) });
                        setServicioFirma({ disponible: true, tokens: 0 });
                    } catch {
                        setServicioFirma({ disponible: false, tokens: 0 });
                    }
                } else {
                    setServicioFirma({ disponible: false, tokens: 0 });
                }
            }
        };
        checkFirma();
        const interval = setInterval(checkFirma, 5000);
        return () => clearInterval(interval);
    }, []);

    const handleFirmarConToken = async () => {
        const pin = firmaDialog.pin;
        if (!pin) { enqueueSnackbar('Ingrese el PIN del token', { variant: 'warning' }); return; }

        setFirmaDialog(prev => ({ ...prev, firmando: true }));
        try {
            // 1. Descargar PDF sin firmar
            const pdfBlob = await panelFirmaDescargarPdfJWT(doc.Id, false);
            console.log(`[Firma] PDF descargado: ${pdfBlob.size} bytes, tipo: ${pdfBlob.type}`);

            // 2. Firmar con el servicio local (exe + token USB)
            const firmaResp = await fetch(`${FIRMA_SERVICE_URL}/firmar?pin=${encodeURIComponent(pin)}`, {
                method: 'POST', body: pdfBlob, headers: { 'Content-Type': 'application/pdf' }
            });
            if (!firmaResp.ok) {
                const errData = await firmaResp.json().catch(() => null);
                console.error('[Firma] Error servicio local:', firmaResp.status, errData);
                throw new Error(errData?.error || `Error al firmar con token (${firmaResp.status})`);
            }
            const signedBlob = await firmaResp.blob();
            console.log(`[Firma] PDF firmado: ${signedBlob.size} bytes`);

            // 3. Subir PDF firmado al backend (marca FIRMADO, crea movimiento, actualiza partes, notifica)
            const upResp = await panelFirmaSubirPdfFirmadoJWT(doc.Id, signedBlob);
            console.log('[Firma] Respuesta upload:', upResp);
            if (!upResp?.ok) throw new Error(upResp?.mensaje || 'Error al subir PDF firmado');

            enqueueSnackbar('Carta poder firmada y registrada correctamente', { variant: 'success' });
            setFirmaDialog({ open: false, pin: '', firmando: false });
            onRefresh?.();
            onVolver();
        } catch (err) {
            console.error('[Firma] Error completo:', err);
            enqueueSnackbar(err.message || 'Error al firmar', { variant: 'error' });
            setFirmaDialog(prev => ({ ...prev, firmando: false }));
        }
    };

    const getEstadoChip = () => {
        if (doc.Estado === 'FIRMADO') return <Chip label="Firmado" color="success" />;
        if (doc.Estado === 'RECHAZADO') return <Chip label="Rechazado" color="error" />;
        return <Chip label="Pendiente" color="warning" />;
    };

    const formatFecha = (f) => {
        if (!f) return '-';
        try { return new Date(f).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
        catch { return f; }
    };

    return (
        <Box sx={styles.root}>
            {/* Header */}
            <Box sx={styles.header}>
                <Tooltip title="Volver a la lista">
                    <IconButton onClick={onVolver} style={{ color: '#fff' }}><ArrowBackIcon /></IconButton>
                </Tooltip>
                <Typography variant="h5" style={{ fontWeight: 700, flex: 1, color: '#fff' }}>
                    Carta Poder {doc.Numero || ''}
                </Typography>
                {getEstadoChip()}
            </Box>

            {/* Metadata */}
            <Paper style={{ padding: 12, borderRadius: 4 }}>
                <Box sx={styles.metaRow}>
                    <Typography variant="body2"><strong>Tipo:</strong> {doc.Tipo || 'CARTA_PODER'}</Typography>
                    <Typography variant="body2"><strong>Solicitante:</strong> {doc.Solicitante || '-'}</Typography>
                    <Typography variant="body2"><strong>Fecha:</strong> {formatFecha(doc.Fecha_Solicitud)}</Typography>
                    {doc.Abogado_Nombre && (
                        <Typography variant="body2"><strong>Abogado:</strong> {doc.Abogado_Nombre}</Typography>
                    )}
                </Box>
                {doc.Estado === 'FIRMADO' && (
                    <Typography variant="body2" color="success.main">
                        Firmado por {doc.Firmado_Por} el {formatFecha(doc.Fecha_Firma)}
                    </Typography>
                )}
                {doc.Estado === 'RECHAZADO' && (
                    <Typography variant="body2" color="error">
                        Rechazado por {doc.Rechazado_Por}: {doc.Motivo_Rechazo}
                    </Typography>
                )}
            </Paper>

            {/* Split view: Document + Context */}
            <Box sx={styles.splitView}>
                {/* Left: Document Preview */}
                <Box sx={styles.documentPanel}>
                    <Paper sx={styles.documentPreview}>
                        <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                            Vista previa del documento
                        </Typography>
                        <Divider style={{ marginBottom: 12 }} />
                        {doc.EscritoContenido ? (
                            <div dangerouslySetInnerHTML={{ __html: doc.EscritoContenido }} />
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No hay contenido HTML disponible
                            </Typography>
                        )}
                    </Paper>

                    {/* Actions */}
                    {doc.Estado === 'PENDIENTE' && (
                        <Paper sx={styles.actionsBar}>
                            {doc.Estado_Firma === 0 && (
                                <Button
                                    variant="contained" color="primary"
                                    startIcon={generandoPdf ? <CircularProgress size={18} /> : <DescriptionIcon />}
                                    onClick={onGenerarPdf} disabled={generandoPdf}
                                >
                                    Generar PDF
                                </Button>
                            )}
                            {doc.Estado_Firma >= 1 && (
                                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => onDescargarPdf(false)}>
                                    Descargar PDF
                                </Button>
                            )}
                            {doc.Estado_Firma >= 1 && servicioFirma.disponible && servicioFirma.tokens > 0 && (
                                <Button
                                    variant="contained"
                                    style={{ backgroundColor: '#2e7d32', color: 'white' }}
                                    startIcon={<TokenIcon />}
                                    onClick={() => setFirmaDialog({ open: true, pin: '', firmando: false })}
                                >
                                    Firmar con Token USB
                                </Button>
                            )}
                            {doc.Estado_Firma >= 1 && (servicioFirma.disponible === false || (servicioFirma.disponible && servicioFirma.tokens === 0)) && (
                                <Tooltip title={servicioFirma.disponible ? "Token no detectado. Inserte el token USB." : "El servicio de firma local no está disponible. Verifique que SIGIF Firma Local esté ejecutándose."}>
                                    <span>
                                        <Button variant="outlined" style={{ borderColor: '#ccc', color: '#999' }}
                                            startIcon={<TokenIcon />} disabled>
                                            Firma Digital no disponible
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                            <Button
                                variant="outlined" color="error"
                                startIcon={<CancelIcon />}
                                onClick={onRechazar}
                            >
                                Rechazar
                            </Button>
                        </Paper>
                    )}
                    {doc.Estado === 'FIRMADO' && (
                        <Paper sx={styles.actionsBar}>
                            <Button variant="contained" color="success" startIcon={<DownloadIcon />} onClick={() => onDescargarPdf(true)}>
                                Descargar PDF Firmado
                            </Button>
                            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => onDescargarPdf(false)}>
                                Descargar Original
                            </Button>
                        </Paper>
                    )}
                </Box>

                {/* Right: Expediente Context */}
                <Box sx={styles.contextPanel}>
                    <ContextoExpediente idExpediente={idExpediente} />
                </Box>
            </Box>

            {/* Firma con Token dialog */}
            <Dialog open={firmaDialog.open} onClose={() => !firmaDialog.firmando && setFirmaDialog({ open: false, pin: '', firmando: false })} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <TokenIcon style={{ color: '#2e7d32' }} />
                        Firmar Carta Poder {doc.Numero || ''}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography paragraph variant="body2">
                        Se firmará digitalmente con el token USB y se registrará el movimiento en el expediente.
                    </Typography>
                    <TextField
                        label="PIN del Token" type="password" fullWidth autoFocus
                        value={firmaDialog.pin}
                        onChange={(e) => setFirmaDialog(prev => ({ ...prev, pin: e.target.value }))}
                        disabled={firmaDialog.firmando}
                        onKeyDown={(e) => e.key === 'Enter' && !firmaDialog.firmando && handleFirmarConToken()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFirmaDialog({ open: false, pin: '', firmando: false })} disabled={firmaDialog.firmando}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        style={{ backgroundColor: '#2e7d32', color: 'white' }}
                        onClick={handleFirmarConToken}
                        disabled={firmaDialog.firmando || !firmaDialog.pin}
                        startIcon={firmaDialog.firmando ? <CircularProgress size={18} color="inherit" /> : <TokenIcon />}
                    >
                        {firmaDialog.firmando ? 'Firmando...' : 'Firmar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CartaPoderDetalle;
